import { Children, isValidElement, type ReactNode, type ReactElement } from 'react';
import './Timeline.css';

export interface TimelineProps {
  /** Index of the active item (everything before/at this index gets the `--active` modifier). */
  active?: number;
  bulletSize?: number;
  lineWidth?: number;
  /** Color of the active bullet/connector. */
  color?: string;
  reverseActive?: boolean;
  children?: ReactNode;
  className?: string;
}

export interface TimelineItemProps {
  bullet?: ReactNode;
  title?: ReactNode;
  /** Active modifier override (else inherits from parent index). */
  active?: boolean;
  children?: ReactNode;
}

function Item({ children }: TimelineItemProps): ReactNode { return <>{children}</>; }
Item.displayName = 'TimelineItem';

interface TimelineComponent extends React.FC<TimelineProps> { Item: typeof Item; }

function TimelineRoot({ active = -1, bulletSize = 22, lineWidth = 2, color, reverseActive, children, className }: TimelineProps): ReactNode {
  const items = Children.toArray(children).filter(
    (c) => isValidElement(c) && (c.type as { displayName?: string }).displayName === 'TimelineItem'
  ) as ReactElement<TimelineItemProps>[];

  return (
    <ul
      className={['rr-timeline', className].filter(Boolean).join(' ')}
      style={{
        ['--rr-timeline-bullet-size' as string]: `${bulletSize}px`,
        ['--rr-timeline-line-width' as string]: `${lineWidth}px`,
        ['--rr-timeline-color' as string]: color ?? 'var(--rr-color-primary)'
      } as React.CSSProperties}
    >
      {items.map((item, i) => {
        const isActive = item.props.active ?? (reverseActive ? i >= active : i <= active);
        return (
          <li key={i} className={`rr-timeline__item ${isActive ? 'rr-timeline__item--active' : ''}`}>
            <span className="rr-timeline__bullet" aria-hidden>
              {item.props.bullet}
            </span>
            <div className="rr-timeline__content">
              {item.props.title ? <div className="rr-timeline__title">{item.props.title}</div> : null}
              <div className="rr-timeline__body">{item.props.children}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export const Timeline = TimelineRoot as TimelineComponent;
Timeline.Item = Item;
